import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Mail,
  Briefcase,
  FileText,
  Users,
  Star,
  Headphones,
  LogOut,
  Plus,
  Trash2,
  Edit,
  Eye,
  Check,
  X,
  ChevronRight,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LOGO_URL from '../../assets/images/Dgitaal_logo.png';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const menuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'contact_messages', label: 'Berichten', icon: Mail },
  { id: 'jobs', label: 'Vacatures', icon: Briefcase },
  { id: 'sollicitaties', label: 'Sollicitaties', icon: Users },
  { id: 'news', label: 'Nieuws', icon: FileText },
  { id: 'partners', label: 'Partners', icon: Users },
  { id: 'referenties', label: 'Referenties', icon: Star },
  { id: 'support', label: 'Support', icon: Headphones },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const downloadCV = async (path, filename) => {
    try {
      // Normalize the path - convert backslashes to forward slashes
      const normalizedPath = path.replace(/\\/g, '/');
      
      // Extract just the storage path part (e.g., "storage/app/cv/filename.docx")
      const storageMatch = normalizedPath.match(/storage[\/\\]app.*/);
      const storagePath = storageMatch ? storageMatch[0].replace(/\\/g, '/') : normalizedPath;
      
      // Construct the download URL using the backend base URL
      const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}${storagePath}`;
      
      const res = await fetch(downloadUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
      toast.success('CV gedownload');
    } catch (err) {
      console.error('Download failed:', err);
      toast.error(`Download mislukt: ${err.message}`);
    }
  };

  const getAuthHeaders = useCallback(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin');
      return null;
    }
    return { Authorization: `Basic ${auth}` };
  }, [navigate]);

  const fetchStats = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      const response = await axios.get(`${API}/admin/stats`, { headers });
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        sessionStorage.removeItem('adminAuth');
        navigate('/admin');
      }
    }
  }, [getAuthHeaders, navigate]);

  const fetchData = useCallback(
    async (tab) => {
      const headers = getAuthHeaders();
      if (!headers) return;

      setLoading(true);
      try {
        let endpoint = tab;
        if (tab === 'news') endpoint = 'news';
        const response = await axios.get(`${API}/admin/${endpoint}`, {
          headers,
        });
        setData(response.data);
      } catch (error) {
        toast.error('Fout bij ophalen data');
      } finally {
        setLoading(false);
      }
    },
    [getAuthHeaders],
  );

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (activeTab !== 'dashboard') {
      fetchData(activeTab);
    }
  }, [activeTab, fetchData]);

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    navigate('/admin');
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('Weet u zeker dat u dit wilt verwijderen?')) return;

    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      let endpoint = type;
      if (type === 'news') endpoint = 'news';
      await axios.delete(`${API}/admin/${endpoint}/${id}`, { headers });
      toast.success('Verwijderd');
      fetchData(activeTab);
      fetchStats();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleMarkRead = async (id) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      await axios.put(
        `${API}/admin/contact_messages/${id}/read`,
        {},
        { headers },
      );
      fetchData('contact_messages');
      fetchStats();
    } catch (error) {
      toast.error('Fout bij markeren als gelezen');
    }
  };

  const handleUpdateStatus = async (id, status) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      await axios.put(
        `${API}/admin/support/${id}/status?status=${status}`,
        {},
        { headers },
      );
      toast.success('Status bijgewerkt');
      fetchData('support');
      fetchStats();
    } catch (error) {
      toast.error('Fout bij bijwerken status');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-BE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className='admin-page min-h-screen' data-testid='admin-dashboard'>
      {/* Sidebar */}
      <aside className='admin-sidebar fixed left-0 top-0 bottom-0 w-72 text-white'>
        <div className='p-6 border-b border-white/10'>
          <img src={LOGO_URL} alt='Dgitaal' className='h-8 w-auto' />
          <p className='text-white/60 text-sm mt-2'>Admin Panel</p>
        </div>

        <nav className='p-4 space-y-1'>
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setActiveTab(item.id);
                setShowForm(false);
                setEditItem(null);
              }}
              className={`admin-nav-item ${activeTab === item.id ? 'active' : 'text-white/70'}`}
              data-testid={`admin-nav-${item.id}`}>
              <item.icon className='w-5 h-5' />
              {item.label}
              {item.id === 'contact_messages' && stats?.contacts?.unread > 0 && (
                <span className='ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full'>
                  {stats.contacts.unread}
                </span>
              )}
              {item.id === 'support' && stats?.support?.open > 0 && (
                <span className='ml-auto bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full'>
                  {stats.support.open}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className='absolute bottom-0 left-0 right-0 p-4 border-t border-white/10'>
          <button
            onClick={handleLogout}
            className='admin-nav-item text-white/70'
            data-testid='admin-logout'>
            <LogOut className='w-5 h-5' />
            Uitloggen
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className='ml-72 p-8 lg:p-10'>
        {/* Dashboard */}
        {activeTab === 'dashboard' && (
          <div>
            <h1 className='text-3xl font-bold text-[#16224b] mb-8'>
              Dashboard
            </h1>

            {stats && (
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
                <div className='admin-stat-card'>
                  <div className='flex items-center justify-between mb-4'>
                    <Mail className='w-8 h-8 text-blue-500' />
                    {stats.contacts.unread > 0 && (
                      <span className='bg-red-100 text-red-600 text-xs font-semibold px-2 py-1 rounded'>
                        {stats.contacts.unread} nieuw
                      </span>
                    )}
                  </div>
                  <p className='text-3xl font-bold text-[#16224b]'>
                    {stats.contacts.total}
                  </p>
                  <p className='text-[#6b7184]'>Berichten</p>
                </div>

                <div className='admin-stat-card'>
                  <Briefcase className='w-8 h-8 text-amber-500 mb-4' />
                  <p className='text-3xl font-bold text-[#16224b]'>
                    {stats.jobs}
                  </p>
                  <p className='text-[#6b7184]'>Actieve vacatures</p>
                </div>

                <div className='admin-stat-card'>
                  <Users className='w-8 h-8 text-green-500 mb-4' />
                  <p className='text-3xl font-bold text-[#16224b]'>
                    {stats.sollicitaties}
                  </p>
                  <p className='text-[#6b7184]'>Sollicitaties</p>
                </div>

                <div className='admin-stat-card'>
                  <div className='flex items-center justify-between mb-4'>
                    <Headphones className='w-8 h-8 text-red-500' />
                    {stats.support.open > 0 && (
                      <span className='bg-amber-100 text-amber-600 text-xs font-semibold px-2 py-1 rounded'>
                        {stats.support.open} open
                      </span>
                    )}
                  </div>
                  <p className='text-3xl font-bold text-[#16224b]'>
                    {stats.support.open}
                  </p>
                  <p className='text-[#6b7184]'>Support tickets</p>
                </div>
              </div>
            )}

            <div className='grid lg:grid-cols-2 gap-6'>
              <div className='admin-surface p-6'>
                <h2 className='font-semibold text-[#16224b] mb-4'>
                  Snelle acties
                </h2>
                <div className='space-y-2'>
                  <button
                    onClick={() => {
                      setActiveTab('jobs');
                      setShowForm(true);
                    }}
                    className='w-full text-left px-4 py-3 rounded-lg hover:bg-[#f8f9fd] flex items-center gap-3 text-[#16224b] transition-colors'>
                    <Plus className='w-5 h-5 text-[#3d56a6]' />
                    Nieuwe vacature toevoegen
                    <ChevronRight className='w-4 h-4 ml-auto' />
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('news');
                      setShowForm(true);
                    }}
                    className='w-full text-left px-4 py-3 rounded-lg hover:bg-[#f8f9fd] flex items-center gap-3 text-[#16224b] transition-colors'>
                    <Plus className='w-5 h-5 text-[#3d56a6]' />
                    Nieuw artikel schrijven
                    <ChevronRight className='w-4 h-4 ml-auto' />
                  </button>
                  <button
                    onClick={() => setActiveTab('contact_messages')}
                    className='w-full text-left px-4 py-3 rounded-lg hover:bg-[#f8f9fd] flex items-center gap-3 text-[#16224b] transition-colors'>
                    <Mail className='w-5 h-5 text-[#3d56a6]' />
                    Berichten bekijken
                    <ChevronRight className='w-4 h-4 ml-auto' />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contacts */}
        {activeTab === 'contact_messages' && (
          <div>
            <h1 className='text-3xl font-bold text-[#16224b] mb-8'>
              Berichten
            </h1>

            {loading ? (
              <p className='text-slate-500'>Laden...</p>
            ) : data.length === 0 ? (
              <p className='text-slate-500'>Geen berichten</p>
            ) : (
              <div className='space-y-4'>
                {data.map((contact) => (
                  <div
                    key={contact.id}
                    className={`admin-surface p-6 ${!contact.gelezen ? 'border-l-4 border-[#3d56a6]' : ''}`}>
                    <div className='flex items-start justify-between mb-4'>
                      <div>
                        <h3 className='font-semibold text-[#16224b]'>
                          {contact.naam}
                        </h3>
                        <p className='text-slate-500 text-sm'>
                          {contact.email}
                        </p>
                      </div>
                      <div className='flex items-center gap-2'>
                        <span className='text-xs text-slate-400'>
                          {formatDate(contact.created_at)}
                        </span>
                        {!contact.gelezen && (
                          <button
                            onClick={() => handleMarkRead(contact.id)}
                            className='admin-ghost-action text-green-600 hover:bg-green-50'
                            title='Markeer als gelezen'>
                            <Check className='w-4 h-4' />
                          </button>
                        )}
                        <button
                          onClick={() =>
                            handleDelete(contact.id, 'contact_messages')
                          }
                          className='admin-ghost-action text-red-600 hover:bg-red-50'>
                          <Trash2 className='w-4 h-4' />
                        </button>
                      </div>
                    </div>
                    <p className='text-sm text-slate-600 mb-2'>
                      <strong>Onderwerp:</strong> {contact.onderwerp}
                    </p>
                    {contact.bedrijf && (
                      <p className='text-sm text-slate-600 mb-2'>
                        <strong>Bedrijf:</strong> {contact.bedrijf}
                      </p>
                    )}
                    {contact.telefoon && (
                      <p className='text-sm text-slate-600 mb-2'>
                        <strong>Telefoon:</strong> {contact.telefoon}
                      </p>
                    )}
                    <p className='text-slate-700 mt-4 p-4 bg-[#f8f9fd] rounded-lg'>
                      {contact.bericht}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Jobs */}
        {activeTab === 'jobs' && (
          <div>
            <div className='flex items-center justify-between mb-8'>
              <h1 className='text-3xl font-bold text-[#16224b]'>Vacatures</h1>
              <Button
                onClick={() => {
                  setShowForm(true);
                  setEditItem(null);
                }}
                className='btn-primary'>
                <Plus className='w-5 h-5 mr-2' />
                Nieuwe Vacature
              </Button>
            </div>

            {showForm && (
              <JobForm
                item={editItem}
                onClose={() => {
                  setShowForm(false);
                  setEditItem(null);
                }}
                onSave={() => {
                  setShowForm(false);
                  setEditItem(null);
                  fetchData('jobs');
                  fetchStats();
                }}
                getAuthHeaders={getAuthHeaders}
              />
            )}

            {!showForm &&
              (loading ? (
                <p className='text-slate-500'>Laden...</p>
              ) : data.length === 0 ? (
                <p className='text-slate-500'>Geen vacatures</p>
              ) : (
                <div className='space-y-4'>
                  {data.map((job) => (
                    <div
                      key={job.id}
                      className='admin-surface p-6'>
                      <div className='flex items-start justify-between'>
                        <div>
                          <div className='flex items-center gap-3 mb-2'>
                            <h3 className='font-semibold text-[#16224b]'>
                              {job.titel}
                            </h3>
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${job.actief ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                              {job.actief ? 'Actief' : 'Inactief'}
                            </span>
                          </div>
                          <p className='text-slate-500 text-sm'>
                            {job.locatie} • {job.type}
                          </p>
                        </div>
                        <div className='flex items-center gap-2'>
                          <button
                            onClick={() => {
                              setEditItem(job);
                              setShowForm(true);
                            }}
                            className='admin-ghost-action text-[#3d56a6] hover:bg-[#eef2ff]'>
                            <Edit className='w-4 h-4' />
                          </button>
                          <button
                            onClick={() => handleDelete(job.id, 'jobs')}
                            className='admin-ghost-action text-red-600 hover:bg-red-50'>
                            <Trash2 className='w-4 h-4' />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
          </div>
        )}

        {/* Sollicitaties */}
        {activeTab === 'sollicitaties' && (
          <div>
            <h1 className='text-3xl font-bold text-[#16224b] mb-8'>
              Sollicitaties
            </h1>

            {loading ? (
              <p className='text-slate-500'>Laden...</p>
            ) : data.length === 0 ? (
              <p className='text-slate-500'>Geen sollicitaties</p>
            ) : (
              <div className='space-y-4'>
                {data.map((sol) => (
                  <div
                    key={sol.id}
                    className='admin-surface p-6'>
                    <div className='flex items-start justify-between mb-4'>
                      <div>
                        <h3 className='font-semibold text-[#16224b]'>
                          {sol.naam}
                        </h3>
                        <p className='text-slate-500 text-sm'>{sol.email}</p>
                        <p className='text-[#3d56a6] text-sm mt-1'>
                          Vacature: {sol.job_titel || 'Onbekend'}
                        </p>
                      </div>
                      <div className='flex items-center gap-2'>
                        <span className='text-xs text-slate-400'>
                          {formatDate(sol.created_at)}
                        </span>
                        <button
                          onClick={() => handleDelete(sol.id, 'sollicitaties')}
                          className='admin-ghost-action text-red-600 hover:bg-red-50'>
                          <Trash2 className='w-4 h-4' />
                        </button>
                      </div>
                    </div>
                    {sol.telefoon && (
                      <p className='text-sm text-slate-600 mb-2'>
                        <strong>Telefoon:</strong> {sol.telefoon}
                      </p>
                    )}
                    {sol.cv_filename && (
                      <button
                        onClick={() => downloadCV(sol.cv_path, sol.cv_filename)}
                        className='text-[#3d56a6] underline text-sm mb-2'>
                        <strong>Download CV</strong>
                      </button>
                    )}
                    <p className='text-slate-700 mt-4 p-4 bg-[#f8f9fd] rounded-lg'>
                      {sol.motivatie}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* News */}
        {activeTab === 'news' && (
          <div>
            <div className='flex items-center justify-between mb-8'>
              <h1 className='text-3xl font-bold text-[#16224b]'>Nieuws</h1>
              <Button
                onClick={() => {
                  setShowForm(true);
                  setEditItem(null);
                }}
                className='btn-primary'>
                <Plus className='w-5 h-5 mr-2' />
                Nieuw Artikel
              </Button>
            </div>

            {showForm && (
              <NewsForm
                item={editItem}
                onClose={() => {
                  setShowForm(false);
                  setEditItem(null);
                }}
                onSave={() => {
                  setShowForm(false);
                  setEditItem(null);
                  fetchData('news');
                }}
                getAuthHeaders={getAuthHeaders}
              />
            )}

            {!showForm &&
              (loading ? (
                <p className='text-slate-500'>Laden...</p>
              ) : data.length === 0 ? (
                <p className='text-slate-500'>Geen artikelen</p>
              ) : (
                <div className='grid md:grid-cols-2 gap-4'>
                  {data.map((article) => (
                    <div
                      key={article.id}
                      className='admin-surface p-6'>
                      <div className='flex items-start justify-between mb-4'>
                        <div>
                          <h3 className='font-semibold text-[#16224b]'>
                            {article.titel}
                          </h3>
                          <p className='text-slate-500 text-sm'>
                            {article.categorie}
                          </p>
                        </div>
                        <div className='flex items-center gap-2'>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${article.gepubliceerd ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                            {article.gepubliceerd ? 'Gepubliceerd' : 'Concept'}
                          </span>
                          <button
                            onClick={() => {
                              setEditItem(article);
                              setShowForm(true);
                            }}
                            className='admin-ghost-action text-[#3d56a6] hover:bg-[#eef2ff]'>
                            <Edit className='w-4 h-4' />
                          </button>
                          <button
                            onClick={() => handleDelete(article.id, 'news')}
                            className='admin-ghost-action text-red-600 hover:bg-red-50'>
                            <Trash2 className='w-4 h-4' />
                          </button>
                        </div>
                      </div>
                      <p className='text-slate-600 text-sm line-clamp-2'>
                        {article.samenvatting}
                      </p>
                    </div>
                  ))}
                </div>
              ))}
          </div>
        )}

        {/* Partners */}
        {activeTab === 'partners' && (
          <div>
            <div className='flex items-center justify-between mb-8'>
              <h1 className='text-3xl font-bold text-[#16224b]'>Partners</h1>
              <Button
                onClick={() => {
                  setShowForm(true);
                  setEditItem(null);
                }}
                className='btn-primary'>
                <Plus className='w-5 h-5 mr-2' />
                Nieuwe Partner
              </Button>
            </div>

            {showForm && (
              <PartnerForm
                item={editItem}
                onClose={() => {
                  setShowForm(false);
                  setEditItem(null);
                }}
                onSave={() => {
                  setShowForm(false);
                  setEditItem(null);
                  fetchData('partners');
                }}
                getAuthHeaders={getAuthHeaders}
              />
            )}

            {!showForm &&
              (loading ? (
                <p className='text-slate-500'>Laden...</p>
              ) : data.length === 0 ? (
                <p className='text-slate-500'>Geen partners</p>
              ) : (
                <div className='grid md:grid-cols-3 gap-4'>
                  {data.map((partner) => (
                    <div
                      key={partner.id}
                      className='admin-surface p-6'>
                      <div className='flex items-start justify-between mb-4'>
                        <h3 className='font-semibold text-[#16224b]'>
                          {partner.naam}
                        </h3>
                        <div className='flex items-center gap-2'>
                          <button
                            onClick={() => {
                              setEditItem(partner);
                              setShowForm(true);
                            }}
                            className='admin-ghost-action text-[#3d56a6] hover:bg-[#eef2ff]'>
                            <Edit className='w-4 h-4' />
                          </button>
                          <button
                            onClick={() => handleDelete(partner.id, 'partners')}
                            className='admin-ghost-action text-red-600 hover:bg-red-50'>
                            <Trash2 className='w-4 h-4' />
                          </button>
                        </div>
                      </div>
                      <p className='text-slate-600 text-sm'>
                        {partner.beschrijving}
                      </p>
                    </div>
                  ))}
                </div>
              ))}
          </div>
        )}

        {/* Referenties */}
        {activeTab === 'referenties' && (
          <div>
            <div className='flex items-center justify-between mb-8'>
              <h1 className='text-3xl font-bold text-[#16224b]'>Referenties</h1>
              <Button
                onClick={() => {
                  setShowForm(true);
                  setEditItem(null);
                }}
                className='btn-primary'>
                <Plus className='w-5 h-5 mr-2' />
                Nieuwe Referentie
              </Button>
            </div>

            {showForm && (
              <ReferentieForm
                item={editItem}
                onClose={() => {
                  setShowForm(false);
                  setEditItem(null);
                }}
                onSave={() => {
                  setShowForm(false);
                  setEditItem(null);
                  fetchData('referenties');
                }}
                getAuthHeaders={getAuthHeaders}
              />
            )}

            {!showForm &&
              (loading ? (
                <p className='text-slate-500'>Laden...</p>
              ) : data.length === 0 ? (
                <p className='text-slate-500'>Geen referenties</p>
              ) : (
                <div className='space-y-4'>
                  {data.map((ref) => (
                    <div
                      key={ref.id}
                      className='admin-surface p-6'>
                      <div className='flex items-start justify-between mb-4'>
                        <div>
                          <h3 className='font-semibold text-[#16224b]'>
                            {ref.contactpersoon}
                          </h3>
                          <p className='text-slate-500 text-sm'>
                            {ref.functie} - {ref.bedrijf}
                          </p>
                        </div>
                        <div className='flex items-center gap-2'>
                          <button
                            onClick={() => {
                              setEditItem(ref);
                              setShowForm(true);
                            }}
                            className='admin-ghost-action text-[#3d56a6] hover:bg-[#eef2ff]'>
                            <Edit className='w-4 h-4' />
                          </button>
                          <button
                            onClick={() => handleDelete(ref.id, 'referenties')}
                            className='admin-ghost-action text-red-600 hover:bg-red-50'>
                            <Trash2 className='w-4 h-4' />
                          </button>
                        </div>
                      </div>
                      <p className='text-slate-700 italic'>"{ref.quote}"</p>
                    </div>
                  ))}
                </div>
              ))}
          </div>
        )}

        {/* Support */}
        {activeTab === 'support' && (
          <div>
            <h1 className='text-3xl font-bold text-[#16224b] mb-8'>
              Support Tickets
            </h1>

            {loading ? (
              <p className='text-slate-500'>Laden...</p>
            ) : data.length === 0 ? (
              <p className='text-slate-500'>Geen support tickets</p>
            ) : (
              <div className='space-y-4'>
                {data.map((ticket) => (
                  <div
                    key={ticket.id}
                    className={`admin-surface p-6 border-l-4 ${
                      ticket.prioriteit === 'Kritiek'
                        ? 'border-red-500'
                        : ticket.prioriteit === 'Hoog'
                          ? 'border-amber-500'
                          : ticket.prioriteit === 'Normaal'
                            ? 'border-blue-500'
                            : 'border-green-500'
                    }`}>
                    <div className='flex items-start justify-between mb-4'>
                      <div>
                        <h3 className='font-semibold text-[#16224b]'>
                          {ticket.naam}
                        </h3>
                        <p className='text-slate-500 text-sm'>{ticket.email}</p>
                      </div>
                      <div className='flex items-center gap-2'>
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            ticket.prioriteit === 'Kritiek'
                              ? 'bg-red-100 text-red-700'
                              : ticket.prioriteit === 'Hoog'
                                ? 'bg-amber-100 text-amber-700'
                                : ticket.prioriteit === 'Normaal'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-green-100 text-green-700'
                          }`}>
                          {ticket.prioriteit}
                        </span>
                        <select
                          value={ticket.status}
                          onChange={(e) =>
                            handleUpdateStatus(ticket.id, e.target.value)
                          }
                          className='admin-select text-xs min-h-0 px-2 py-1'>
                          <option value='open'>Open</option>
                          <option value='in_behandeling'>In behandeling</option>
                          <option value='opgelost'>Opgelost</option>
                          <option value='gesloten'>Gesloten</option>
                        </select>
                        <button
                          onClick={() => handleDelete(ticket.id, 'support')}
                          className='admin-ghost-action text-red-600 hover:bg-red-50'>
                          <Trash2 className='w-4 h-4' />
                        </button>
                      </div>
                    </div>
                    {ticket.telefoon && (
                      <p className='text-sm text-slate-600 mb-2'>
                        <strong>Telefoon:</strong> {ticket.telefoon}
                      </p>
                    )}
                    <p className='text-slate-700 mt-4 p-4 bg-[#f7f8fb] rounded-lg'>
                      {ticket.beschrijving}
                    </p>
                    <p className='text-xs text-slate-400 mt-2'>
                      {formatDate(ticket.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

// Form Components
function JobForm({ item, onClose, onSave, getAuthHeaders }) {
  const [form, setForm] = useState(
    item || {
      titel: '',
      locatie: '',
      type: 'Fulltime',
      beschrijving: '',
      vereisten: [''],
      aanbod: [''],
      actief: true,
    },
  );
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const headers = getAuthHeaders();
    if (!headers) return;

    setSaving(true);
    try {
      const data = {
        ...form,
        vereisten: form.vereisten.filter((v) => v),
        aanbod: form.aanbod.filter((a) => a),
      };
      if (item) {
        await axios.put(`${API}/admin/jobs/${item.id}`, data, { headers });
      } else {
        await axios.post(`${API}/admin/jobs`, data, { headers });
      }
      toast.success(item ? 'Vacature bijgewerkt' : 'Vacature aangemaakt');
      onSave();
    } catch (error) {
      toast.error('Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='admin-surface p-6 mb-8'>
      <div className='flex items-center justify-between mb-6'>
        <h2 className='text-lg font-semibold'>
          {item ? 'Vacature bewerken' : 'Nieuwe vacature'}
        </h2>
        <button onClick={onClose} className='admin-ghost-action'>
          <X className='w-5 h-5' />
        </button>
      </div>
      <form onSubmit={handleSubmit} className='space-y-4'>
        <div className='grid md:grid-cols-2 gap-4'>
          <div>
            <Label>Titel</Label>
            <Input
              value={form.titel}
              onChange={(e) => setForm({ ...form, titel: e.target.value })}
              required
            />
          </div>
          <div>
            <Label>Locatie</Label>
            <Input
              value={form.locatie}
              onChange={(e) => setForm({ ...form, locatie: e.target.value })}
              required
            />
          </div>
        </div>
        <div className='grid md:grid-cols-2 gap-4'>
          <div>
            <Label>Type</Label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className='admin-select'>
              <option>Fulltime</option>
              <option>Parttime</option>
              <option>Freelance</option>
              <option>Stage</option>
            </select>
          </div>
          <div className='flex items-center gap-2 pt-6'>
            <input
              type='checkbox'
              checked={form.actief}
              onChange={(e) => setForm({ ...form, actief: e.target.checked })}
            />
            <Label>Actief</Label>
          </div>
        </div>
        <div>
          <Label>Beschrijving</Label>
          <Textarea
            value={form.beschrijving}
            onChange={(e) => setForm({ ...form, beschrijving: e.target.value })}
            rows={3}
            required
          />
        </div>
        <div>
          <Label>Vereisten (één per regel)</Label>
          <Textarea
            value={form.vereisten.join('\n')}
            onChange={(e) =>
              setForm({ ...form, vereisten: e.target.value.split('\n') })
            }
            rows={4}
          />
        </div>
        <div>
          <Label>Aanbod (één per regel)</Label>
          <Textarea
            value={form.aanbod.join('\n')}
            onChange={(e) =>
              setForm({ ...form, aanbod: e.target.value.split('\n') })
            }
            rows={4}
          />
        </div>
        <div className='flex gap-2'>
          <Button type='submit' disabled={saving} className='btn-primary'>
            {saving ? 'Opslaan...' : 'Opslaan'}
          </Button>
          <Button type='button' onClick={onClose} variant='outline' className='admin-outline-button'>
            Annuleren
          </Button>
        </div>
      </form>
    </div>
  );
}

function NewsForm({ item, onClose, onSave, getAuthHeaders }) {
  const [form, setForm] = useState(
    item || {
      titel: '',
      samenvatting: '',
      inhoud: '',
      categorie: 'Blog',
      afbeelding: '',
      gepubliceerd: true,
    },
  );
  const [saving, setSaving] = useState(false);

  const editor = useEditor({
    extensions: [StarterKit],
    content: form.inhoud,
    onUpdate: ({ editor }) => {
      setForm({ ...form, inhoud: editor.getHTML() });
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const headers = getAuthHeaders();
    if (!headers) return;

    setSaving(true);
    try {
      if (item) {
        await axios.put(`${API}/admin/news/${item.id}`, form, { headers });
      } else {
        await axios.post(`${API}/admin/news`, form, { headers });
      }
      toast.success(item ? 'Artikel bijgewerkt' : 'Artikel aangemaakt');
      onSave();
    } catch (error) {
      toast.error('Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='admin-surface p-6 mb-8'>
      <div className='flex items-center justify-between mb-6'>
        <h2 className='text-lg font-semibold'>
          {item ? 'Artikel bewerken' : 'Nieuw artikel'}
        </h2>
        <button onClick={onClose} className='admin-ghost-action'>
          <X className='w-5 h-5' />
        </button>
      </div>
      <form onSubmit={handleSubmit} className='space-y-4'>
        <div>
          <Label>Titel</Label>
          <Input
            value={form.titel}
            onChange={(e) => setForm({ ...form, titel: e.target.value })}
            required
          />
        </div>
        <div className='grid md:grid-cols-2 gap-4'>
          <div>
            <Label>Categorie</Label>
            <select
              value={form.categorie}
              onChange={(e) => setForm({ ...form, categorie: e.target.value })}
              className='admin-select'>
              <option>Blog</option>
              <option>Bedrijfsnieuws</option>
              <option>Diensten</option>
              <option>Tips</option>
            </select>
          </div>
          <div className='flex items-center gap-2 pt-6'>
            <input
              type='checkbox'
              checked={form.gepubliceerd}
              onChange={(e) =>
                setForm({ ...form, gepubliceerd: e.target.checked })
              }
            />
            <Label>Gepubliceerd</Label>
          </div>
        </div>
        <div>
          <Label>Afbeelding URL</Label>
          <Input
            value={form.afbeelding || ''}
            onChange={(e) => setForm({ ...form, afbeelding: e.target.value })}
            placeholder='https://...'
          />
        </div>
        <div>
          <Label>Samenvatting</Label>
          <Textarea
            value={form.samenvatting}
            onChange={(e) => setForm({ ...form, samenvatting: e.target.value })}
            rows={2}
            required
          />
        </div>
        <div>
          <Label>Inhoud</Label>
          <div className='border border-slate-300 rounded-md overflow-hidden mt-2'>
            <div className='admin-editor-toolbar p-2 flex flex-wrap gap-1'>
              <button
                type='button'
                onClick={() => editor?.chain().focus().toggleBold().run()}
                className={`admin-editor-button font-semibold ${editor?.isActive('bold') ? 'active' : ''}`}
                title='Bold'
              >
                B
              </button>
              <button
                type='button'
                onClick={() => editor?.chain().focus().toggleItalic().run()}
                className={`admin-editor-button italic ${editor?.isActive('italic') ? 'active' : ''}`}
                title='Italic'
              >
                I
              </button>
              <button
                type='button'
                onClick={() => editor?.chain().focus().toggleStrike().run()}
                className={`admin-editor-button line-through ${editor?.isActive('strike') ? 'active' : ''}`}
                title='Strike'
              >
                S
              </button>
              <div className='border-l border-slate-300 mx-1'></div>
              <button
                type='button'
                onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                className={`admin-editor-button font-semibold ${editor?.isActive('heading', { level: 2 }) ? 'active' : ''}`}
                title='Heading 2'
              >
                H2
              </button>
              <button
                type='button'
                onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                className={`admin-editor-button font-semibold ${editor?.isActive('heading', { level: 3 }) ? 'active' : ''}`}
                title='Heading 3'
              >
                H3
              </button>
              <div className='border-l border-slate-300 mx-1'></div>
              <button
                type='button'
                onClick={() => editor?.chain().focus().toggleBulletList().run()}
                className={`admin-editor-button ${editor?.isActive('bulletList') ? 'active' : ''}`}
                title='Bullet List'
              >
                • List
              </button>
              <button
                type='button'
                onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                className={`admin-editor-button ${editor?.isActive('orderedList') ? 'active' : ''}`}
                title='Ordered List'
              >
                1. List
              </button>
              <button
                type='button'
                onClick={() => editor?.chain().focus().toggleBlockquote().run()}
                className={`admin-editor-button ${editor?.isActive('blockquote') ? 'active' : ''}`}
                title='Blockquote'
              >
                &quot;
              </button>
              <button
                type='button'
                onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
                className={`admin-editor-button ${editor?.isActive('codeBlock') ? 'active' : ''}`}
                title='Code Block'
              >
                &lt;/&gt;
              </button>
              <div className='border-l border-slate-300 mx-1'></div>
              <button
                type='button'
                onClick={() => editor?.chain().focus().clearNodes().run()}
                className='admin-editor-button'
                title='Clear'
              >
                Clear
              </button>
            </div>
            <div className='bg-white p-4 min-h-96'>
              <EditorContent editor={editor} />
            </div>
          </div>
        </div>
        <div className='flex gap-2'>
          <Button type='submit' disabled={saving} className='btn-primary'>
            {saving ? 'Opslaan...' : 'Opslaan'}
          </Button>
          <Button type='button' onClick={onClose} variant='outline' className='admin-outline-button'>
            Annuleren
          </Button>
        </div>
      </form>
    </div>
  );
}

function PartnerForm({ item, onClose, onSave, getAuthHeaders }) {
  const [form, setForm] = useState(
    item || { naam: '', beschrijving: '', logo: '', website: '', actief: true },
  );
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const headers = getAuthHeaders();
    if (!headers) return;

    setSaving(true);
    try {
      if (item) {
        await axios.put(`${API}/admin/partners/${item.id}`, form, { headers });
      } else {
        await axios.post(`${API}/admin/partners`, form, { headers });
      }
      toast.success(item ? 'Partner bijgewerkt' : 'Partner aangemaakt');
      onSave();
    } catch (error) {
      toast.error('Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='admin-surface p-6 mb-8'>
      <div className='flex items-center justify-between mb-6'>
        <h2 className='text-lg font-semibold'>
          {item ? 'Partner bewerken' : 'Nieuwe partner'}
        </h2>
        <button onClick={onClose} className='admin-ghost-action'>
          <X className='w-5 h-5' />
        </button>
      </div>
      <form onSubmit={handleSubmit} className='space-y-4'>
        <div>
          <Label>Naam</Label>
          <Input
            value={form.naam}
            onChange={(e) => setForm({ ...form, naam: e.target.value })}
            required
          />
        </div>
        <div>
          <Label>Beschrijving</Label>
          <Textarea
            value={form.beschrijving}
            onChange={(e) => setForm({ ...form, beschrijving: e.target.value })}
            rows={2}
            required
          />
        </div>
        <div>
          <Label>Logo URL</Label>
          <Input
            value={form.logo || ''}
            onChange={(e) => setForm({ ...form, logo: e.target.value })}
            placeholder='https://...'
          />
        </div>
        <div>
          <Label>Website</Label>
          <Input
            value={form.website || ''}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            placeholder='https://...'
          />
        </div>
        <div className='flex items-center gap-2'>
          <input
            type='checkbox'
            checked={form.actief}
            onChange={(e) => setForm({ ...form, actief: e.target.checked })}
          />
          <Label>Actief</Label>
        </div>
        <div className='flex gap-2'>
          <Button type='submit' disabled={saving} className='btn-primary'>
            {saving ? 'Opslaan...' : 'Opslaan'}
          </Button>
          <Button type='button' onClick={onClose} variant='outline' className='admin-outline-button'>
            Annuleren
          </Button>
        </div>
      </form>
    </div>
  );
}

function ReferentieForm({ item, onClose, onSave, getAuthHeaders }) {
  const [form, setForm] = useState(
    item || {
      bedrijf: '',
      contactpersoon: '',
      functie: '',
      quote: '',
      actief: true,
    },
  );
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const headers = getAuthHeaders();
    if (!headers) return;

    setSaving(true);
    try {
      if (item) {
        await axios.put(`${API}/admin/referenties/${item.id}`, form, {
          headers,
        });
      } else {
        await axios.post(`${API}/admin/referenties`, form, { headers });
      }
      toast.success(item ? 'Referentie bijgewerkt' : 'Referentie aangemaakt');
      onSave();
    } catch (error) {
      toast.error('Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='admin-surface p-6 mb-8'>
      <div className='flex items-center justify-between mb-6'>
        <h2 className='text-lg font-semibold'>
          {item ? 'Referentie bewerken' : 'Nieuwe referentie'}
        </h2>
        <button onClick={onClose} className='admin-ghost-action'>
          <X className='w-5 h-5' />
        </button>
      </div>
      <form onSubmit={handleSubmit} className='space-y-4'>
        <div className='grid md:grid-cols-2 gap-4'>
          <div>
            <Label>Contactpersoon</Label>
            <Input
              value={form.contactpersoon}
              onChange={(e) =>
                setForm({ ...form, contactpersoon: e.target.value })
              }
              required
            />
          </div>
          <div>
            <Label>Functie</Label>
            <Input
              value={form.functie}
              onChange={(e) => setForm({ ...form, functie: e.target.value })}
              required
            />
          </div>
        </div>
        <div>
          <Label>Bedrijf</Label>
          <Input
            value={form.bedrijf}
            onChange={(e) => setForm({ ...form, bedrijf: e.target.value })}
            required
          />
        </div>
        <div>
          <Label>Quote</Label>
          <Textarea
            value={form.quote}
            onChange={(e) => setForm({ ...form, quote: e.target.value })}
            rows={3}
            required
          />
        </div>
        <div className='flex items-center gap-2'>
          <input
            type='checkbox'
            checked={form.actief}
            onChange={(e) => setForm({ ...form, actief: e.target.checked })}
          />
          <Label>Actief</Label>
        </div>
        <div className='flex gap-2'>
          <Button type='submit' disabled={saving} className='btn-primary'>
            {saving ? 'Opslaan...' : 'Opslaan'}
          </Button>
          <Button type='button' onClick={onClose} variant='outline' className='admin-outline-button'>
            Annuleren
          </Button>
        </div>
      </form>
    </div>
  );
}
